/**
 * The identity-provider and sign-in-routing entries of the activity log (doc
 * 28d §10.3), on the Identity Providers page: domains added, verified (or the
 * DNS check failed), attested, removed; routing changes with their preview
 * counts; and the sign-in refusals the API records (domain mismatch,
 * unverified email). Read from GET /activity (resourceType
 * access_identity_provider), newest first. The same labels are used on the
 * Audit Log page.
 */
import { useCallback, useEffect, useState } from 'react';
import { History, RefreshCw } from 'lucide-react';
import activityService, { type ActivityRecord } from '../../services/activity.service';
import { useAuthStore } from '../../stores/authStore';
import { useTenantStore } from '../../stores/tenantStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { USER_TYPES } from '../../utils/constants';
import { formatDate } from '../../utils/formatDate';
import { describeSignInRoutingActivity, signInRoutingActivityTone } from '../../utils/signInRouting';

const PAGE_SIZE = 15;

const IdpActivityCard = ({ refreshKey }: { refreshKey: number }) => {
  const user = useAuthStore((s) => s.user);
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const [rows, setRows] = useState<ActivityRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // `/activity` is excluded from the api.ts tenant injection, and the API
  // returns an empty page to a SuperAdmin who names no tenant — so the
  // selected tenant is passed explicitly.
  const tenantId = user?.type === USER_TYPES.SUPER_ADMIN || user?.type === USER_TYPES.CHANNEL_ADMIN ? selectedTenantId : undefined;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const r = await activityService.list({
        page: 1,
        pageSize: PAGE_SIZE,
        resourceType: 'access_identity_provider',
        ...(tenantId ? { tenantId } : {}),
      });
      setRows(r?.items ?? []);
    } catch {
      setError('The activity log could not be read.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  return (
    <div className="card space-y-2" data-testid="idp-activity">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <History className="w-4 h-4 text-primary-600" />
          Recent identity-provider activity
          <span className="text-xs font-normal text-gray-400">from the activity log</span>
        </h2>
        <button type="button" className="p-1.5 rounded hover:bg-gray-100 text-gray-500" title="Refresh" onClick={() => void load()}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      {error ? (
        <p className="text-xs text-danger-700">{error}</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-400">{loading ? 'Loading…' : 'No identity-provider activity yet.'}</p>
      ) : (
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-3">Time</th>
              <th className="py-2 pr-3">Who</th>
              <th className="py-2 pr-3">Action</th>
              <th className="py-2 pr-3">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => {
              const d = describeSignInRoutingActivity(r);
              return (
                <tr key={r.id} data-testid="idp-activity-row" data-action={r.action}>
                  <td className="whitespace-nowrap py-1.5 pr-3 text-xs text-gray-500">{formatDate(r.created_at, dateSettings)}</td>
                  <td className="py-1.5 pr-3 text-xs text-gray-700">
                    {r.user_email ?? (r.user_id === 'anonymous' ? 'sign-in' : r.user_id === 'system' ? 'system (24 h re-check)' : r.user_id)}
                  </td>
                  <td className="py-1.5 pr-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${signInRoutingActivityTone(r.action)}`}
                      title={r.action}
                    >
                      {d?.label ?? r.action}
                    </span>
                    {r.status === 'failure' && !/failed|refused/i.test(d?.label ?? '') && <span className="ml-1 text-xs text-danger-700">failed</span>}
                  </td>
                  <td className="py-1.5 pr-3 text-xs text-gray-700 break-all" data-testid="idp-activity-summary">{d?.summary ?? r.resource_name ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default IdpActivityCard;
